import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BrowserRouter as Router,
  Link,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import Axios from "axios";
import {
  FaBus,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaClock,
  FaMapMarkerAlt,
  FaRoute,
  FaChevronRight,
  FaUsers,
  FaIdCard,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import SelectClass from "../ChildComponent/SelectClass";
import TransportStudentDetails from "./TransportStudentDetails";
import AddTransportStudent from "./AddTransportStudent";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function studentInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function formatPickupTime(raw) {
  if (!raw) return "—";
  const value = String(raw).trim();
  const m = value.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return value;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h)) return value;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="ts-card__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="ts-card__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const TransportStudentCard = memo(function TransportStudentCard({
  student,
  index,
  onOpen,
}) {
  const transport = student?.transport_info?.[0] || null;
  const rollNo = student?.class?.[0]?.student_roll_no || "—";
  const vehicleName = transport?.vehicle_name || transport?.vehicle_number || "—";
  const vehicleDetail = [
    transport?.vehicle_type,
    transport?.vehicle_number && transport?.vehicle_name
      ? transport.vehicle_number
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Link
      to="/AdminMain/TransportStudentDetails"
      className="ts-card"
      style={{ "--ts-i": index }}
      onClick={() => onOpen(student)}
    >
      <span className="ts-card__index" aria-hidden>
        {String(index + 1).padStart(2, "0")}
      </span>

      <StudentAvatar
        name={student.stu_name}
        imagePath={student.student_img_path}
      />

      <div className="ts-card__main min-w-0">
        <div className="ts-card__top">
          <div className="ts-card__who min-w-0">
            <h3 className="ts-card__name">{student.stu_name || "N/A"}</h3>
            <p className="ts-card__father">
              {student.father_name ? `S/O ${student.father_name}` : "—"}
            </p>
          </div>
        </div>

        <div className="ts-card__chips">
          <span className="ts-chip ts-chip--enroll">
            <FaIdCard aria-hidden />
            {student.student_enrollment_no || "N/A"}
            <span className="ts-chip__sep" aria-hidden>
              ·
            </span>
            Roll {rollNo}
          </span>
          <span className="ts-chip ts-chip--route" title={transport?.route_name}>
            <FaRoute aria-hidden />
            {transport?.route_name || "No route"}
          </span>
          <span
            className="ts-chip ts-chip--vehicle"
            title={vehicleDetail || vehicleName}
          >
            <FaBus aria-hidden />
            {vehicleName}
          </span>
          <span
            className="ts-chip ts-chip--point"
            title={transport?.pickup_point_name}
          >
            <FaMapMarkerAlt aria-hidden />
            {transport?.pickup_point_name || "No pickup"}
          </span>
          <span className="ts-chip ts-chip--time">
            <FaClock aria-hidden />
            {formatPickupTime(transport?.pickup_time)}
          </span>
        </div>
      </div>

      <span className="ts-card__go">
        Details <FaChevronRight aria-hidden />
      </span>
    </Link>
  );
});

const TransportStudent = () => {
  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);
  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const SERVICE_ID = "SRV001";

  const getTransportStudent = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        setStudents([]);
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        const response = await Axios.get(`${API_BASE()}getTransportStudent.php`, {
          params: {
            school_code: schoolCode,
            class_id: classId,
            section_id: sectionId,
            session_id: sessionId,
            service_id: SERVICE_ID,
          },
        });

        if (response.data?.api_status === "OK") {
          setStudents(response.data.data || []);
        } else {
          setStudents([]);
        }
      } catch (error) {
        console.error("Error fetching transport students:", error);
        setStudents([]);
        myToast.notify_warning("Network error loading transport students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    if (selectedClassPosition >= 0 && classId && sectionId && sessionId) {
      getTransportStudent();
    }
  }, [
    selectedClassPosition,
    classId,
    sectionId,
    sessionId,
    getTransportStudent,
  ]);

  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position,
      class_name,
      section_name,
      _session_name,
      START_DATE
    ) => {
      setClassId(class_id);
      setClassName(class_name || "");
      setSectionName(section_name || "");
      setSectionId(section_id);
      setSessionId(session_id);
      setStartDate(START_DATE || "");
      setSelectedClassPosition(selected_position);
      setSearchTerm("");
    },
    []
  );

  const onClickedStudent = useCallback(
    (student) => {
      localStorage.setItem("TransportData", JSON.stringify(student));
      localStorage.setItem("START_DATE", startDate);
    },
    [startDate]
  );

  const onClickedAddTransport = useCallback(() => {
    localStorage.setItem("START_DATE", startDate);
  }, [startDate]);

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((student) => {
      const transport = student?.transport_info?.[0] || {};
      const hay = [
        student.stu_name,
        student.student_enrollment_no,
        student.father_name,
        student.class?.[0]?.student_roll_no,
        transport.vehicle_name,
        transport.vehicle_number,
        transport.pickup_point_name,
        transport.route_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const classSelected = selectedClassPosition >= 0 && classId && sectionId;
  const classLabel = [className, sectionName].filter(Boolean).join(" · ");

  const listView = (
    <div
      className="content payroll-page sm-page ts-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Transport Student</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Transport Students</h1>
              <p className="sm-hero__sub">
                View and manage students assigned to transport
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="ts-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search transport students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              onClick={() => getTransportStudent({ soft: true })}
              disabled={refreshing || !classSelected}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh list"
              aria-label="Refresh list"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link
              to="/AdminMain/AddTransportStudent"
              className="ts-hero-link ts-hero-link--solid"
              onClick={onClickedAddTransport}
            >
              <FaPlus aria-hidden /> Add Student
            </Link>
            <Link
              to="/AdminMain/AddTransportStudentMultiple"
              className="ts-hero-link"
            >
              <FaUsers aria-hidden /> Add Multiple
            </Link>
          </div>
        </header>

        <div className="ts-content">
          <div className="ts-class-wrap">
            <SelectClass
              action={callBackClassSelect}
              selected_position={selectedClassPosition}
            />
          </div>

          <div className="ts-section-head">
            <div className="min-w-0">
              <h2 className="ts-section-head__title">Assigned students</h2>
              <p className="ts-section-head__sub">
                {classSelected
                  ? classLabel || "Selected class"
                  : "Choose a class to load the list"}
              </p>
            </div>
            <span className="ts-section-head__count">
              {classSelected
                ? `${filteredStudents.length}${
                    searchTerm.trim() ? ` of ${students.length}` : ""
                  } student${filteredStudents.length === 1 ? "" : "s"}`
                : "—"}
            </span>
          </div>

          {loading ? (
            <div className="ts-loading">
              <MyLoader />
            </div>
          ) : !classSelected ? (
            <div className="sm-empty ts-empty">
              <div className="ts-empty__circle" aria-hidden>
                <FaBus />
              </div>
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section above to view transport students.
              </p>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="sm-empty ts-empty">
              <div className="ts-empty__circle" aria-hidden>
                <FaBus />
              </div>
              <p className="sm-empty__title">
                {students.length === 0
                  ? "No transport students found"
                  : "No matches for your search"}
              </p>
              <p className="sm-empty__hint">
                {students.length === 0
                  ? "No students are assigned to transport for this class."
                  : "Try a different name, enrollment, vehicle, or route."}
              </p>
              {students.length === 0 ? (
                <Link
                  to="/AdminMain/AddTransportStudent"
                  className="ts-empty-cta"
                  onClick={onClickedAddTransport}
                >
                  <FaPlus aria-hidden /> Add Student
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="ts-list">
              {filteredStudents.map((student, idx) => (
                <TransportStudentCard
                  key={
                    student.student_enrollment_no ||
                    student.stu_id ||
                    `student-${idx}`
                  }
                  student={student}
                  index={idx}
                  onOpen={onClickedStudent}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Router>
      <Switch>
        <Route exact path="/AdminMain/TransportStudent">
          {listView}
        </Route>
        <Route
          path="/AdminMain/TransportStudentDetails"
          component={TransportStudentDetails}
        />
        <Route
          path="/AdminMain/AddTransportStudent"
          component={AddTransportStudent}
        />
      </Switch>
    </Router>
  );
};

export default TransportStudent;
